/**
 * Contain Scale Utility
 *
 * Fabric.js-independent utility for calculating contain-like scaling.
 * Used by both frontend (design scaling) and backend (placement calculator).
 */

/**
 * Calculate the scale factor to fit content within a container (contain behavior)
 *
 * This is equivalent to CSS `object-fit: contain` - the content is scaled
 * to fit completely within the container while maintaining aspect ratio.
 * The smaller scale factor is used so nothing overflows.
 *
 * @param contentWidth - Original width of the content
 * @param contentHeight - Original height of the content
 * @param containerWidth - Maximum allowed width
 * @param containerHeight - Maximum allowed height
 * @returns Scale factor to apply to both dimensions
 *
 * @example
 * ```typescript
 * // Landscape image in portrait container
 * const scale = containScale(2000, 1000, 500, 600);
 * // scale = 0.25 (limited by width: 500/2000)
 * // Result: 500x250px
 *
 * // Portrait image in portrait container
 * const scale = containScale(1000, 2000, 500, 600);
 * // scale = 0.30 (limited by height: 600/2000)
 * // Result: 300x600px
 * ```
 */
export function containScale(
  contentWidth: number,
  contentHeight: number,
  containerWidth: number,
  containerHeight: number
): number {
  // Guard against division by zero
  const safeContentWidth = Math.max(contentWidth, 1);
  const safeContentHeight = Math.max(contentHeight, 1);

  const scaleX = containerWidth / safeContentWidth;
  const scaleY = containerHeight / safeContentHeight;

  // Use smaller scale so content fits completely (no overflow)
  return Math.min(scaleX, scaleY);
}

/**
 * Calculate the scale factor to fill a container (cover behavior)
 *
 * This is equivalent to CSS `object-fit: cover` - the content is scaled
 * to completely cover the container while maintaining aspect ratio.
 * The larger scale factor is used so the container is filled.
 *
 * @param contentWidth - Original width of the content
 * @param contentHeight - Original height of the content
 * @param containerWidth - Width to fill
 * @param containerHeight - Height to fill
 * @returns Scale factor to apply to both dimensions
 *
 * @example
 * ```typescript
 * // Landscape image covering portrait container
 * const scale = coverScale(2000, 1000, 500, 600);
 * // scale = 0.60 (limited by height: 600/1000)
 * // Result: 1200x600px (overflows horizontally)
 * ```
 */
export function coverScale(
  contentWidth: number,
  contentHeight: number,
  containerWidth: number,
  containerHeight: number
): number {
  // Guard against division by zero
  const safeContentWidth = Math.max(contentWidth, 1);
  const safeContentHeight = Math.max(contentHeight, 1);

  const scaleX = containerWidth / safeContentWidth;
  const scaleY = containerHeight / safeContentHeight;

  // Use larger scale so container is filled completely
  return Math.max(scaleX, scaleY);
}
